import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, LoaderCircle, X } from 'lucide-react';
import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ContextType,
  type Key,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import {
  Button as AriaButton,
  ComboBox as AriaComboBox,
  ComboBoxStateContext,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
} from 'react-aria-components';
import { useI18n } from '../i18n/I18nProvider';
import { useDebouncedValue } from '../lib/hooks';
import buttonStyles from './Button.module.css';
import { cx } from './cx';
import styles from './Field.module.css';
import { FieldHelp, FieldLabel, type FieldChromeProps } from './TextField';

export interface AsyncQuery<T> {
  queryKey: readonly unknown[];
  queryFn: (context: { signal: AbortSignal }) => Promise<T[]>;
  staleTime?: number;
}

export interface AsyncComboBoxProps<T> extends FieldChromeProps {
  /** Key of the selected item. */
  value: string | null;
  /** The selected item itself, so its label stays visible when it is not in the current results. */
  selectedItem: T | null;
  onChange: (item: T | null) => void;
  /** Server search for the typed text ('' = initial suggestions). */
  queryFor: (q: string) => AsyncQuery<T>;
  getKey: (item: T) => string;
  /** Text placed in the input once an item is chosen (also used for typeahead). */
  getLabel: (item: T) => string;
  renderItem?: (item: T) => ReactNode;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
  /** Allows clearing the selection with a button. */
  isClearable?: boolean;
  /**
   * An "add" picker: the parent keeps `value` null and collects each pick elsewhere (chips). After a
   * pick the field empties and its list closes, ready for the next search.
   */
  clearOnPick?: boolean;
  /**
   * When the list opens. `focus` (the default, unchanged): on focus, with the server's initial
   * suggestions for an empty query. `input` (the batch page's add field, WP-1b.3): only after
   * typing — no open button, no suggestions for an empty query, and the arrow keys do not open an
   * empty field.
   */
  opensOn?: 'focus' | 'input';
  'aria-label'?: string;
}

type ComboBoxStateRef = MutableRefObject<ContextType<typeof ComboBoxStateContext>>;

/** Hands React Aria's combobox state to the field, so an add picker can close its list. */
function ComboBoxStateBridge({ stateRef }: { stateRef: ComboBoxStateRef }) {
  const state = useContext(ComboBoxStateContext);
  useEffect(() => {
    stateRef.current = state;
  });
  return null;
}

/**
 * Type-ahead over a server search (customers, items, ETA code tables). Results are not filtered
 * again in the browser: the server's search (Arabic-normalized) is authoritative.
 */
export function AsyncComboBox<T>({
  label,
  description,
  errorMessage,
  isRequired,
  isOptional,
  fieldName,
  value,
  selectedItem,
  onChange,
  queryFor,
  getKey,
  getLabel,
  renderItem,
  placeholder,
  isDisabled,
  className,
  isClearable,
  clearOnPick,
  opensOn = 'focus',
  ...props
}: AsyncComboBoxProps<T>) {
  const { t } = useI18n();
  const typedOnly = opensOn === 'input';
  const selectedLabel = selectedItem ? getLabel(selectedItem) : '';
  const [inputValue, setInputValue] = useState(selectedLabel);
  // The label of the option just picked: until the parent passes the new selection back, the input
  // shows that label; it must not start a search (whose results would re-open the list).
  const justPicked = useRef<string | null>(null);
  const stateRef: ComboBoxStateRef = useRef(null);

  // Keep the input in sync when the selection changes from outside (load, reset, reload).
  useEffect(() => {
    setInputValue(selectedLabel);
  }, [value, selectedLabel]);

  const searchText =
    inputValue === selectedLabel || inputValue === justPicked.current ? '' : inputValue.trim();
  const debounced = useDebouncedValue(searchText, 250);
  const query = queryFor(debounced);
  // `opensOn="input"`: an empty query asks the server nothing and offers nothing.
  const idle = typedOnly && searchText === '';
  const { data, isFetching, isError, isPlaceholderData } = useQuery({
    queryKey: query.queryKey,
    queryFn: ({ signal }) => query.queryFn({ signal }),
    staleTime: query.staleTime ?? 30_000,
    placeholderData: keepPreviousData,
    enabled: !isDisabled && !(typedOnly && debounced === ''),
  });
  // Until the search for the current text has answered, the results on hand belong to an older
  // search and are about to be replaced. They are not offered: a press on a row that moves or is
  // swapped out between pointer down and up is lost and leaves the list open. The list says it is
  // searching instead; the selected item stays in the collection so Escape still reverts to it.
  const isStale = debounced !== searchText || isPlaceholderData;

  const items = useMemo(() => {
    const list = isStale || idle ? [] : (data ?? []);
    if (selectedItem && !list.some((item) => getKey(item) === getKey(selectedItem)))
      return [selectedItem, ...list];
    return list;
  }, [data, isStale, idle, selectedItem, getKey]);

  const byKey = useMemo(() => new Map(items.map((item) => [getKey(item), item])), [items, getKey]);

  return (
    <AriaComboBox
      {...props}
      items={items}
      value={value}
      onChange={(key: Key | null) => {
        if (key === null) {
          if (value !== null) onChange(null);
          return;
        }
        const item = byKey.get(String(key));
        if (item && clearOnPick) {
          // The value stays null, so React Aria would keep the list open and, on Escape, read the
          // leftover text as typing and re-open it. Empty the field and close the list instead.
          setInputValue('');
          onChange(item);
          stateRef.current?.close();
          return;
        }
        if (item) {
          const picked = getLabel(item);
          justPicked.current = picked;
          // Only the re-renders right after the pick are suppressed; a later focus opens normally.
          setTimeout(() => {
            if (justPicked.current === picked) justPicked.current = null;
          }, 1500);
          // Value and input label change in the SAME render: with both controlled, React Aria
          // closes the list on the selection, and a label arriving one render later would look like
          // typing and re-open it.
          setInputValue(picked);
          onChange(item);
        }
      }}
      inputValue={inputValue}
      onInputChange={setInputValue}
      menuTrigger={typedOnly ? 'input' : 'focus'}
      // An empty query of a typed-only field has nothing to show, so its list stays closed.
      allowsEmptyCollection={!idle}
      isDisabled={isDisabled ?? false}
      isInvalid={Boolean(errorMessage)}
      isRequired={isRequired ?? false}
      validationBehavior="aria"
      className={cx(styles.field, className)}
      data-field={fieldName}
    >
      <FieldLabel label={label} isRequired={isRequired ?? false} isOptional={isOptional ?? false} />
      {clearOnPick ? <ComboBoxStateBridge stateRef={stateRef} /> : null}
      <div
        className={styles.control}
        onKeyDownCapture={
          typedOnly
            ? (event) => {
                // Opens only after typing: the arrow keys never open an empty field.
                if (idle && (event.key === 'ArrowDown' || event.key === 'ArrowUp'))
                  event.stopPropagation();
              }
            : undefined
        }
      >
        <Input className={styles.input} placeholder={placeholder} />
        {isFetching ? (
          <span className={styles.adornment} role="status" aria-label={t.common.searching}>
            <LoaderCircle size={16} className={buttonStyles.spinner} aria-hidden="true" />
          </span>
        ) : null}
        {isClearable && value !== null && !isDisabled ? (
          <AriaButton
            className={styles.trigger}
            aria-label={t.common.clearSelection}
            onPress={() => {
              setInputValue('');
              onChange(null);
            }}
          >
            <X size={16} aria-hidden="true" />
          </AriaButton>
        ) : null}
        {typedOnly ? null : (
          <AriaButton className={styles.trigger}>
            <ChevronsUpDown size={16} aria-hidden="true" />
          </AriaButton>
        )}
      </div>
      <FieldHelp description={description} errorMessage={errorMessage} />
      <Popover className={styles.popover} offset={4}>
        <ListBox
          className={styles.listBox}
          renderEmptyState={() => (
            <div className={styles.emptyState}>
              {isError
                ? t.common.searchFailed
                : isFetching || isStale
                  ? t.common.searching
                  : t.common.noMatches}
            </div>
          )}
        >
          {(item: T) => (
            <ListBoxItem id={getKey(item)} textValue={getLabel(item)} className={styles.option}>
              {({ isSelected }) => (
                <>
                  <span className={styles.optionBody}>
                    {renderItem ? renderItem(item) : getLabel(item)}
                  </span>
                  {isSelected ? (
                    <Check className={styles.optionCheck} size={16} aria-hidden="true" />
                  ) : null}
                </>
              )}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaComboBox>
  );
}

export interface ComboBoxOption {
  id: string;
  label: string;
  description?: string;
}

export interface ComboBoxFieldProps extends FieldChromeProps {
  options: ComboBoxOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

/** Searchable choice from a longer local list (countries, currencies). */
export function ComboBoxField({
  label,
  description,
  errorMessage,
  isRequired,
  isOptional,
  fieldName,
  options,
  value,
  onChange,
  placeholder,
  isDisabled,
  className,
  ...props
}: ComboBoxFieldProps) {
  const { t } = useI18n();
  return (
    <AriaComboBox
      {...props}
      defaultItems={options}
      value={value}
      onChange={(key: Key | null) => onChange(key === null ? null : String(key))}
      menuTrigger="focus"
      isDisabled={isDisabled ?? false}
      isInvalid={Boolean(errorMessage)}
      isRequired={isRequired ?? false}
      validationBehavior="aria"
      className={cx(styles.field, className)}
      data-field={fieldName}
    >
      <FieldLabel label={label} isRequired={isRequired ?? false} isOptional={isOptional ?? false} />
      <div className={styles.control}>
        <Input className={styles.input} placeholder={placeholder} />
        <AriaButton className={styles.trigger}>
          <ChevronsUpDown size={16} aria-hidden="true" />
        </AriaButton>
      </div>
      <FieldHelp description={description} errorMessage={errorMessage} />
      <Popover className={styles.popover} offset={4}>
        <ListBox
          className={styles.listBox}
          renderEmptyState={() => <div className={styles.emptyState}>{t.common.noMatches}</div>}
        >
          {(option: ComboBoxOption) => (
            <ListBoxItem id={option.id} textValue={option.label} className={styles.option}>
              {({ isSelected }) => (
                <>
                  <span className={styles.optionBody}>
                    <span className={styles.optionTitle}>{option.label}</span>
                    {option.description ? (
                      <span className={styles.optionMeta}>{option.description}</span>
                    ) : null}
                  </span>
                  {isSelected ? (
                    <Check className={styles.optionCheck} size={16} aria-hidden="true" />
                  ) : null}
                </>
              )}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaComboBox>
  );
}
